import axios from "axios";
import RequestCancelled from "./RequestCancelled";
import store from "../vuex/store";

const baseURL = process.env.VUE_APP_ARVORE_URL;

export default class Api {
    constructor() {
        this.http = axios.create({
            baseURL: baseURL,
            withCredentials: true,
            headers: {},
        });

        this.http.interceptors.request.use((config) => {
            const usuarioInstitucionalAtivo =
                store.getters["autenticacao/usuarioInstitucionalAtivo"];

            if (usuarioInstitucionalAtivo) {
                config.headers["X-Usuario-Institucional-Id"] =
                    usuarioInstitucionalAtivo.id;
            }

            return config;
        });

        this.http.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401) {
                    store.commit(
                        "autenticacao/setUsuarioInstitucionalAtivo",
                        null,
                    );
                    const redirectUri = encodeURIComponent(window.location.origin);
                    window.location.href = `${baseURL}/auth/login?redirect_uri=${redirectUri}`;
                }
                throw error;
            }
        );
    }

    /**
     * Creates and returns a new Axios CancelToken source.
     * This can be used to cancel ongoing Axios requests.
     *
     * @returns {CancelTokenSource} An Axios CancelToken source object.
     */
    createCancelToken() {
        return axios.CancelToken.source();
    }

    /**
     * Cancels an ongoing GET request using the provided cancel token.
     *
     * @param {Object} cancelToken - The cancel token object.
     * @param {Object} cancelToken.token - The token used to identify the request.
     * @param {Function} cancelToken.cancel - The function to call to cancel the request.
     */
    cancelGetRequest(cancelToken) {
        if (cancelToken && cancelToken.token) {
            cancelToken.cancel("Request cancelled by user.");
        }
    }

    /**
     * Sends a GET request to the specified URL.
     *
     * @param {string} url - The URL to send the GET request to.
     * @param {Object} [cancelToken] - Optional Axios CancelToken object to allow request cancellation.
     * @returns {Promise<Object>} A promise that resolves with the response data or rejects with an error.
     * @throws {RequestCancelled} If the request is cancelled using the cancel token.
     * @throws {Error} If the request fails for any other reason.
     */
    get(url, cancelToken) {
        if (cancelToken) {
            return this.http
                .get(url, { cancelToken: cancelToken.token })
                .catch((error) => {
                    if (axios.isCancel(error)) {
                        throw new RequestCancelled(error.message);
                    }
                    throw error;
                });
        }
        return this.http.get(url);
    }

    /**
     * Sends a POST request to the specified URL with the provided data.
     *
     * @param {string} url - The endpoint URL to which the POST request is sent.
     * @param {Object} data - The payload to be sent in the body of the POST request.
     * @returns {Promise} A promise that resolves with the response of the POST request.
     */
    post(url, data) {
        const headers = {
            "Content-Type": "application/json",
        };

        return this.http.post(url, data, { headers });
    }

    /**
     * Sends a PUT request to the specified URL with the provided data.
     *
     * @param {string} url - The endpoint URL to send the PUT request to.
     * @param {Object} data - The data to be sent in the request body.
     * @returns {Promise} A promise that resolves with the response of the HTTP PUT request.
     */
    put(url, data) {
        const headers = {
            "Content-Type": "application/json",
        };

        return this.http.put(url, data, { headers });
    }

    /**
     * Sends a PATCH request to the specified URL with the provided data.
     *
     * @param {string} url - The endpoint URL to send the PATCH request to.
     * @param {Object} data - The data to be sent in the PATCH request body.
     * @returns {Promise} A promise that resolves with the response of the PATCH request.
     */
    patch(url, data) {
        const headers = {
            "Content-Type": "application/json",
        };
        return this.http.patch(url, data, { headers });
    }

    /**
     * Sends a DELETE request to the specified URL with the provided data and headers.
     *
     * @param {string} url - The endpoint URL to send the DELETE request to.
     * @param {Object} data - The data to be sent as the request body.
     * @returns {Promise} A promise that resolves with the response of the DELETE request.
     */
    delete(url, data) {
        const headers = {
            "Content-Type": "application/json",
        };
        return this.http.delete(url, data, { headers });
    }
}
